import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

/**
 * Footer social row, shown under the brand description. Order, icons and labels live here; the URLs come from Site Settings
 * (Admin → Settings → Social). A platform with no URL saved is simply not rendered — nothing is invented, so the row only ever
 * contains real Shingare profiles. Only http(s) URLs are accepted (a stray `javascript:` value can't become a link).
 */
const PLATFORMS = [
  { key: "social_linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "social_facebook", label: "Facebook", Icon: Facebook },
  { key: "social_instagram", label: "Instagram", Icon: Instagram },
  { key: "social_youtube", label: "YouTube", Icon: Youtube },
];

const isWebUrl = (u) => typeof u === "string" && /^https?:\/\/\S+$/i.test(u.trim());

export default function FooterSocialLinks({ settings }) {
  const links = PLATFORMS.map((p) => ({ ...p, href: settings?.[p.key]?.trim() })).filter((p) => isWebUrl(p.href));
  if (!links.length) return null;
  return (
    <ul className="mt-6 flex flex-wrap items-center gap-2.5" aria-label="Shingare Industries on social media" data-testid="footer-social">
      {links.map(({ key, label, Icon, href }, i) => (
        <li key={key} className="relative">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Shingare Industries on ${label}`}
            data-testid={`footer-social-${i}`}
            className="peer flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] text-white/75 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#1769FF] hover:bg-[#1769FF] hover:text-white hover:shadow-[0_10px_24px_-8px_rgba(23,105,255,0.85)] focus-visible:-translate-y-0.5 focus-visible:border-[#1769FF] focus-visible:bg-[#1769FF] focus-visible:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7FB0FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050F24]"
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} aria-hidden="true" />
          </a>
          {/* tooltip: purely visual (the link's aria-label already names it), shown on hover and keyboard focus; sits BELOW the icon so it never covers the description */}
          <span aria-hidden="true" className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 -translate-y-1 whitespace-nowrap rounded-md border border-white/10 bg-[#0B2348] px-2.5 py-1 text-[11px] font-medium text-white opacity-0 shadow-lg transition-all duration-200 peer-hover:translate-y-0 peer-hover:opacity-100 peer-focus-visible:translate-y-0 peer-focus-visible:opacity-100 max-md:hidden">
            {label}
          </span>
        </li>
      ))}
    </ul>
  );
}
